import { useEffect, useState } from "react";
import api from "@/api/client";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { BarChart3, Truck, IndianRupee, Scale, Clock, Download, FileSpreadsheet, FileText, Building2, PackagePlus, PackageOpen } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import OperationalReportsSection from "@/components/OperationalReportsSection";
import ReferenceLookupPanel from "@/components/ReferenceLookupPanel";
import SmartBillingSection from "@/components/SmartBillingSection";

const COLORS = ["#BE1E1E", "#00E5FF", "#22c55e", "#eab308", "#0A0A0A", "#a855f7"];

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const num = (n) => Number(n || 0).toLocaleString("en-IN");
const pct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);
const hrs = (n) => (n == null ? "—" : `${n.toFixed(1)}h`);

export default function ReportsPage() {
  const { user } = useAuth();
  const [days, setDays] = useState(30);
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [d, setD] = useState(null);
  const [cr, setCr] = useState(null);

  const canFilterBranch = user && ["company_admin", "super_admin"].includes(user.role);
  const isBranchScoped = user && ["branch_user", "branch_admin"].includes(user.role);

  useEffect(() => {
    if (canFilterBranch) api.get("/branches").then(r => setBranches(r.data)).catch(() => {});
  }, [canFilterBranch]);

  useEffect(() => {
    const params = { days, ...(branchId ? { branch_id: branchId } : {}) };
    api.get("/reports/summary", { params }).then(r => setD(r.data));
    api.get("/reports/courier", { params }).then(r => setCr(r.data));
  }, [days, branchId]);

  if (!d || !cr) return <div className="p-10 overline">Loading…</div>;

  return (
    <div className="p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline flex items-center gap-2"><BarChart3 size={12}/> Operational analytics</div>
          <h1 className="text-4xl font-display mt-1">Reports.</h1>
          {isBranchScoped && (
            <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5" data-testid="branch-scope-note">
              <Building2 size={12} className="text-primary" /> Scoped to your branch only
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButtons days={days} branchId={branchId} />
          {canFilterBranch && branches.length > 0 && (
            <select
              data-testid="report-branch-filter"
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="text-xs mono uppercase tracking-widest border border-border rounded-md px-2 py-1.5 bg-card hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">All branches</option>
              {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <div className="inline-flex border border-border rounded-md overflow-hidden">
            {[7,14,30,90].map(dd => (
              <button key={dd} data-testid={`report-days-${dd}`} onClick={()=>setDays(dd)}
                className={`px-3 py-1.5 text-xs mono uppercase tracking-widest ${days===dd ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{dd}d</button>
            ))}
          </div>
        </div>
      </header>

      {/* Grand totals */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="report-totals">
        <Kpi icon={Truck} label={`Total parcels · ${days}d`} value={num(cr.total.total_count)}
             sub={`${num(cr.total.inward_count)} inward · ${num(cr.total.outward_count)} outward`} />
        <Kpi icon={Scale} label="Total weight" value={`${num(cr.total.total_weight_kg)} kg`} />
        <Kpi icon={IndianRupee} label="Total spend" value={inr(cr.total.total_amount_inr)}
             sub="priority + stored rates" />
        <Kpi icon={Clock} label="Delivered" value={num(cr.total.delivered_count)}
             sub={cr.total.total_count ? `${((cr.total.delivered_count / cr.total.total_count) * 100).toFixed(1)}% completion` : ""} />
      </section>

      <SmartBillingSection branchId={branchId} />

      <OperationalReportsSection branchId={branchId} />

      {/* Inward + Outward manifest & status lookup */}
      <div className="grid grid-cols-1 gap-6" data-testid="manifest-lookup-grid">
        <ReferenceLookupPanel direction="outward" branchId={branchId} />
        <ReferenceLookupPanel direction="inward" branchId={branchId} />
      </div>

      {/* Courier breakdown table */}
      <section className="border border-border bg-card rounded-md" data-testid="courier-report">
        <div className="p-5 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="overline">Courier performance</div>
            <div className="font-display text-xl mt-1">Courier-wise breakdown ({days}d)</div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
            SLA · inward 24h · outward 8h
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left overline">
                <th className="p-3">Courier</th>
                <th className="p-3 text-right">Inward</th>
                <th className="p-3 text-right">Outward</th>
                <th className="p-3 text-right">Total</th>
                <th className="p-3 text-right">Weight (kg)</th>
                <th className="p-3 text-right">INR</th>
                <th className="p-3 text-right">Delivered</th>
                <th className="p-3 text-right">Avg TAT</th>
                <th className="p-3 text-right">SLA %</th>
              </tr>
            </thead>
            <tbody>
              {cr.rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No parcels in this window.</td></tr>
              )}
              {cr.rows.map((r) => {
                const slaTone = r.sla_compliance_pct == null ? "text-muted-foreground" :
                  r.sla_compliance_pct >= 90 ? "text-emerald-700" :
                  r.sla_compliance_pct >= 70 ? "text-amber-700" : "text-primary font-bold";
                return (
                  <tr key={r.courier} className="border-b border-border hover:bg-muted/40" data-testid={`courier-row-${r.courier}`}>
                    <td className="p-3 font-semibold">{r.courier}</td>
                    <td className="p-3 mono text-right">{num(r.inward_count)}</td>
                    <td className="p-3 mono text-right">{num(r.outward_count)}</td>
                    <td className="p-3 mono text-right font-bold text-primary">{num(r.total_count)}</td>
                    <td className="p-3 mono text-right">{num(r.total_weight_kg)}</td>
                    <td className="p-3 mono text-right">{inr(r.total_amount_inr)}</td>
                    <td className="p-3 mono text-right text-emerald-700">{num(r.delivered_count)}</td>
                    <td className="p-3 mono text-right">{hrs(r.avg_tat_hours)}</td>
                    <td className={`p-3 mono text-right ${slaTone}`}>{pct(r.sla_compliance_pct)}</td>
                  </tr>
                );
              })}
              {cr.rows.length > 0 && (
                <tr className="bg-muted/40 font-bold" data-testid="courier-total-row">
                  <td className="p-3">Grand total</td>
                  <td className="p-3 mono text-right">{num(cr.total.inward_count)}</td>
                  <td className="p-3 mono text-right">{num(cr.total.outward_count)}</td>
                  <td className="p-3 mono text-right text-primary">{num(cr.total.total_count)}</td>
                  <td className="p-3 mono text-right">{num(cr.total.total_weight_kg)}</td>
                  <td className="p-3 mono text-right">{inr(cr.total.total_amount_inr)}</td>
                  <td className="p-3 mono text-right text-emerald-700">{num(cr.total.delivered_count)}</td>
                  <td className="p-3 mono text-right">—</td>
                  <td className="p-3 mono text-right">—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="border border-border bg-card p-6 rounded-md">
          <div className="overline">Status distribution</div>
          <div className="font-display text-xl mt-1 mb-4">By status</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={d.by_status} dataKey="count" nameKey="status" innerRadius={60} outerRadius={95}>
                  {d.by_status.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", borderRadius:0 }} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border bg-card p-6 rounded-md">
          <div className="overline">Volume by department</div>
          <div className="font-display text-xl mt-1 mb-4">Top departments</div>
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={d.by_department}>
                <XAxis dataKey="department" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} stroke="hsl(var(--muted-foreground))" />
                <Tooltip contentStyle={{ background:"hsl(var(--card))", border:"1px solid hsl(var(--border))", borderRadius:0 }} />
                <Bar dataKey="count" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="border border-border bg-card rounded-md">
        <div className="p-5 border-b border-border">
          <div className="overline">Recipients</div>
          <div className="font-display text-xl mt-1">Top receivers ({days}d)</div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left overline">
              <th className="p-4">#</th><th className="p-4">Recipient</th><th className="p-4">Parcels</th>
            </tr>
          </thead>
          <tbody>
            {d.top_recipients.map((r, i) => (
              <tr key={i} className="border-b border-border">
                <td className="p-4 mono">{i+1}</td>
                <td className="p-4">{r.recipient}</td>
                <td className="p-4 mono text-primary">{r.count}</td>
              </tr>
            ))}
            {d.top_recipients.length === 0 && <tr><td colSpan={3} className="p-8 text-center text-muted-foreground">No data yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-border rounded-md bg-card p-4">
      <div className="flex items-center justify-between">
        <div className="overline">{label}</div>
        <Icon size={14} className="text-primary" />
      </div>
      <div className="font-display text-3xl mt-2 leading-none">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function ExportButtons({ days, branchId }) {
  const [busy, setBusy] = useState(null);
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const params = { format: fmt, days, ...(branchId ? { branch_id: branchId } : {}) };
      const { data } = await api.get("/reports/courier/export", {
        params, responseType: "blob",
      });
      const url = window.URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `courier-report-${days}d${branchId ? `-${branchId.slice(0,6)}` : ""}.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${fmt.toUpperCase()} export failed`);
    } finally { setBusy(null); }
  };
  const btn = "inline-flex items-center gap-1.5 border border-border rounded-md px-3 py-1.5 text-xs font-semibold hover:border-primary hover:text-primary transition-colors bg-card disabled:opacity-50";
  return (
    <div className="inline-flex items-center gap-1.5" data-testid="report-exports">
      <button data-testid="export-csv" disabled={!!busy} onClick={() => download("csv")} className={btn}>
        <FileText size={12} /> {busy === "csv" ? "…" : "CSV"}
      </button>
      <button data-testid="export-xlsx" disabled={!!busy} onClick={() => download("xlsx")} className={btn}>
        <FileSpreadsheet size={12} /> {busy === "xlsx" ? "…" : "Excel"}
      </button>
      <button data-testid="export-pdf" disabled={!!busy} onClick={() => download("pdf")} className={`${btn} bg-primary/5 border-primary/40 text-primary`}>
        <Download size={12} /> {busy === "pdf" ? "…" : "PDF"}
      </button>
    </div>
  );
}
